import React, {
  ReactElement,
  useEffect,
  useState,
  Fragment,
  useCallback,
} from 'react';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Button as MUIButton,
  DialogContentText,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';

import api from '../../services/api';
import { toaster } from '../../services/toaster';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { SlidingPaneEdit } from '../Page/SlidingPaneEdit';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { file } from '../../schema/file';
import DialogContent from '@mui/material/DialogContent';
import { FileUploader } from 'react-drag-drop-files';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import { serviceProvider } from '../../schema/serviceProvider';
import { MoreFileOptions } from '../Table/assets/MoreFileOptions';
import { RemoveRedEyeRounded } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { LoadingButton } from '@mui/lab';

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};

export const ServiceProviderAttachment = ({
  schema,
  values,
  tableType,
}: {
  schema: any;
  values: any;
  tableType: string;
}): ReactElement => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState<any>(false);
  const [properties, setProperties] = useState<any>([]);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [addingEntity, setAddingEntity] = useState<boolean>(false);
  const [files, setFiles] = useState<any>([]);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);

  const fetchImages = useCallback(() => {
    const searchParams = new URLSearchParams({});

    const prices = api
      .get(`/service-provider-attachments/${values.id}`, searchParams)
      .then((res: any) => {
        setProperties(res?.files);
      });
  }, []);

  const downloadImage = (response: any, fileName: string) => {
    const imageBlob = new Blob([response], { type: response?.type });
    const imageUrl = URL.createObjectURL(imageBlob);
    const link: any = document.createElement('a');
    link.href = imageUrl;
    link.download = fileName ? fileName : 'image.png';
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleDownload = (attachment: any) => {
    const searchParams = new URLSearchParams({
      fileName: attachment?.name,
      path: 'public',
    });
    api
      .getFile(`/download-file`, searchParams)
      .then((res: any) => {
        downloadImage(res, attachment?.name);
      })
      .catch((error) => {
        console.log(error);
        toaster.show({
          icon: 'error',
          message: `Error while download data`,
          intent: 'danger',
        });
      });

    return true;
  };

  const uploadFiles = () => {
    setLoadingButton(true);
    // Create a form and post it to server
    let formData = new FormData();
    files.forEach((file: any, i: any) => {
      formData.append(`file-${i}`, file);
    });

    formData.append(`column`, 'files');
    formData.append(`id`, values?.id);
    formData.append(`schema`, JSON.stringify(serviceProvider));

    const allFiles = [...files];
    api
      .postFile(`/upload-files`, formData)
      .then((res: any) => {
        res.map((item: any) => {
          allFiles.push(item);
        });
        setAddingEntity(false);
        setFiles([]);
        fetchImages();
        toast.success('Uploaded successfully');
      })
      .catch((error: any) => {
        toast.error('Upload failed');
      })
      .finally(() => {
        setLoadingButton(false);
      });
  };

  const handleChangeCheckBox = (event: any, index: any, columnName: string) => {
    const newFiles = [...files];
    newFiles.map((row: any, rowIndex: number) => {
      if (index === rowIndex) {
        row[columnName] = event.target.checked;
      }
      newFiles[rowIndex] = row;
    });
    setFiles(newFiles);
  };

  const handleChange = (newFiles: any) => {
    const newFilesArray = [...newFiles];
    const oldFiles = [...files];
    newFilesArray.map((file: any) => {
      file.preview = URL.createObjectURL(file);
      oldFiles.push(file);
    });
    setFiles(oldFiles);
  };

  const handleDeleteFile = (index: any) => {
    const newFiles = files.filter((row: any, rowIndex: number) => {
      return index !== rowIndex;
    });

    setFiles(newFiles);
  };

  const handleChangeData = (
    event: any,
    index: any,
    columnName: string,
    value: any
  ) => {
    const newFiles = [...files];
    newFiles.map((row: any, rowIndex: number) => {
      if (index === rowIndex) {
        row[columnName] = value;
      }
      newFiles[rowIndex] = row;
    });
    setFiles(newFiles);
  };

  const handleUpdateImage = useCallback(
    async (image: any, data: any) => {
      file.client
        .update(image?.id, data)
        .then((res: any) => {
          const oldProperties = [...properties];
          const newRows = oldProperties.map((row: any) => {
            return row.id === image.id ? res : row;
          });
          setProperties(newRows);
        })
        .catch((e: any) => {
          console.error(e);
        });
    },
    [properties]
  );

  useEffect(() => {
    fetchImages();
  }, [values, schema]);

  return (
    <>
      <Stack
        direction={'row'}
        alignContent={'center'}
        gap={2}
        sx={{
          marginY: '20px',
        }}
      >
        <Stack direction={'row'}>
          <Button
            variant="contained"
            onClick={() => {
              setAddingEntity(true);
            }}
          >
            Upload
          </Button>
        </Stack>
      </Stack>

      <Stack gap={1} width={'100%'}>
        {properties.map((image: any) => {
          return (
            <Card
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: '#fff',
                p: '10px',
                borderRadius: '5px',
              }}
            >
              <MoreFileOptions
                clickOnEdit={() => {
                  setOpenedSlidingPane({
                    schema: file,
                    row: image,
                  });
                }}
                clickOnDelete={() => {
                  setOpenConfirmDialog({
                    row: image,
                    schema: file,
                  });
                }}
                clickOnDownload={() => {
                  handleDownload(image);
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign={'left'}
                sx={{
                  overflowWrap: 'break-word',
                  width: '100%',
                }}
              >
                {image?.name}
              </Typography>
              {(image.type.startsWith('image') ||
                image.type === 'application/pdf') && (
                <>
                  <IconButton href={image.link} target="_blank">
                    <RemoveRedEyeRounded />
                  </IconButton>
                </>
              )}
            </Card>
          );
        })}
      </Stack>

      {addingEntity && (
        <Dialog
          open={addingEntity}
          onClose={() => {
            setAddingEntity(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            <Stack
              direction={'row'}
              alignContent={'center'}
              justifyContent={'space-between'}
            >
              <span>Upload Attachments</span>
              <IconButton
                color={'default'}
                size={'small'}
                onClick={() => {
                  setAddingEntity(false);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <FileUploader
                multiple={true}
                handleChange={handleChange}
                name="file"
                classes={'w-full !max-w-[100%]'}
              />
              <Divider />

              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-start"
                marginBottom={2}
                alignItems={'center'}
              >
                <div className="multiselect-field w-full">
                  <Stack
                    direction="column"
                    spacing={1}
                    justifyContent="flex-start"
                    mt={1}
                  >
                    {files.map((file: any, index: any) => {
                      console.log(file.preview);

                      return (
                        <>
                          <Stack
                            className={'bg-[#f9f9f9]'}
                            direction="row"
                            spacing={1}
                            justifyContent="space-between"
                            mt={2}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems={'center'}
                              mt={2}
                            >
                              <div>
                                <AttachFileRoundedIcon />
                              </div>
                              <Stack direction="column" spacing={0}>
                                <span>{file?.name}</span>
                                <small>
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </small>
                              </Stack>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems={'center'}
                              mt={2}
                            >
                              <IconButton
                                aria-label="delete"
                                size="small"
                                color={'error'}
                                onClick={() => {
                                  handleDeleteFile(index);
                                }}
                                sx={{
                                  padding: '9px',
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          </Stack>
                        </>
                      );
                    })}
                  </Stack>
                </div>
              </Stack>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <LoadingButton
              loading={loadingButton}
              disabled={loadingButton}
              onClick={uploadFiles}
            >
              Upload Files and Close
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Are you sure you want to delete it?'}
        </DialogTitle>
        <DialogActions>
          <MUIButton
            onClick={() => {
              setOpenConfirmDialog(false);
            }}
          >
            Cancel
          </MUIButton>
          <MUIButton
            variant={'contained'}
            color={'error'}
            onClick={() => {
              const deletedRow = openConfirmDialog?.row;

              openConfirmDialog?.schema.client
                .delete(deletedRow.id as number, { id: deletedRow.id })
                .then((res: any) => {
                  const tempProperties = [...properties];
                  const newRows = tempProperties.filter((item: any) => {
                    return item?.id !== deletedRow?.id;
                  });
                  setProperties(newRows);
                  toaster.show({
                    intent: 'success',
                    message: `Successfully deleted Attachment: ${
                      deletedRow?.name ? deletedRow?.name : deletedRow?.id
                    }`,
                  });
                  setOpenConfirmDialog(false);
                })
                .catch((e: any) => {
                  console.log(e);
                  toaster.show({
                    icon: 'error',
                    message: `Error: Unable to delete ${schema.name}: ${
                      deletedRow?.name ? deletedRow?.name : deletedRow?.id
                    }`,
                    intent: 'danger',
                  });
                });
            }}
            autoFocus
          >
            Delete
          </MUIButton>
        </DialogActions>
      </Dialog>

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            fetchImages();
            setOpenedSlidingPane({});
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            setOpenedSlidingPane({});
          }}
        />
      )}
    </>
  );
};
